import type { ReactNode } from "react";
import { useMemo, useState } from "react";
import type { SurveyListed } from "../api/types.js";
import { Async } from "../components/Async.jsx";
import { FirstRunTour } from "../components/FirstRunTour.jsx";
import { IconState } from "../components/Icons.jsx";
import { usePortal } from "../app/context.js";
import { useAsync } from "../app/use-async.js";
import { href } from "../app/router.js";
import { utcMoment } from "../lib/format.js";

/**
 * Every survey — the survey-only portal's one list, and its home.
 *
 * The pipeline this replaces listed JOBS and mentioned that a survey had
 * arrived. That is the right shape for a company running installs and the
 * wrong one for a surveyor, whose whole job is the survey: they opened a board
 * of eleven destinations to find the one row that had come in off the phone.
 *
 * FOUR STAGES, AND ALL FOUR ARE SURVEY WORK. Scanned, answers, ready, handed
 * over. Nothing about money or delivery, because neither is a surveyor's to
 * do and a stage nobody here can advance is a stage that only ever looks
 * stuck.
 *
 * EVERY STAGE IS SAID IN WORDS AS WELL AS COLOUR, and carries a tick or a
 * count. A board that distinguishes its states by hue alone is unreadable to
 * the one in twelve men who cannot separate red from green, and unreadable to
 * anybody at all in bright sun on a tablet, which is where this gets used.
 *
 * THE ADDRESS WARNING IS THE POINT OF THE LIST. `buildSurveyIngestExport`
 * refuses a survey whose job has no address and postcode — the receiving
 * portal keys a property on its address. Before this, a surveyor found that
 * out at the end, after doing all the work. Now the row says so on arrival.
 */

type Stage = "scanned" | "answers" | "ready" | "handed-over";

/** Where a survey has got to, from what the list already knows. */
function stageOf(survey: SurveyListed): Stage {
  if (survey.handedOverAt !== null) return "handed-over";
  if (survey.answersLeft > 0) return "answers";
  if (!survey.hasAddress) return "answers";
  return "ready";
}

/**
 * Whether this reader has never opened the survey's job since it arrived.
 *
 * A moment, not a flag (migration 0068), so a job opened last week and
 * re-surveyed this morning reads as unopened again without anything having to
 * remember to say so.
 */
function isUnopened(survey: SurveyListed): boolean {
  if (survey.syncedAt === null) return false;
  if (survey.lastViewedAt === null) return true;
  return Date.parse(survey.syncedAt) > Date.parse(survey.lastViewedAt);
}

export function Surveys(): ReactNode {
  const { client } = usePortal();
  const { state, reload } = useAsync<readonly SurveyListed[]>(
    () => client.surveys(),
    [client],
  );
  const [find, setFind] = useState("");

  return (
    <>
      <div className="page-header spread">
        <h1>Surveys</h1>
        <button className="secondary" onClick={reload}>
          Refresh
        </button>
      </div>

      {/* Above the list rather than over it, so the work is visible behind
          the explanation of it. Shown once per browser. */}
      <FirstRunTour />

      <Async state={state} what="the surveys" reload={reload}>
        {(surveys) => (
          <List surveys={surveys} find={find} onFind={setFind} />
        )}
      </Async>
    </>
  );
}

type Order = "newest" | "oldest" | "address";
type Show = "all" | "needs-answer" | "ready" | "handed-over";

function List({
  surveys,
  find,
  onFind,
}: {
  readonly surveys: readonly SurveyListed[];
  readonly find: string;
  readonly onFind: (value: string) => void;
}): ReactNode {
  const [order, setOrder] = useState<Order>("newest");
  const [show, setShow] = useState<Show>("all");
  /*
   * NEWEST FIRST IS THE DEFAULT AND STAYS THE DEFAULT.
   *
   * A surveyor opens this to deal with what has just come in off the phone, so
   * the order that needs no thought is the one they get. The others are for
   * going back to something: a house whose name you remember but not when it
   * arrived, or the oldest thing still sitting there.
   *
   * The filter is by WHAT A SURVEY NEEDS, not by a status word. "Needs an
   * answer" and "Ready to send" are the two piles somebody actually sorts
   * into; "draft" and "complete" are what the database happens to call them.
   */
  const matching = useMemo(() => {
    const needle = find.trim().toLowerCase();
    const found =
      needle === ""
        ? surveys
        : surveys.filter((s) =>
            [s.reference, s.addressLine1 ?? "", s.postcode ?? "", s.surveyorName ?? ""]
              .join(" ")
              .toLowerCase()
              .includes(needle),
          );

    const kept = found.filter((s) => {
      if (show === "all") return true;
      const stage = stageOf(s);
      if (show === "needs-answer") return stage === "answers";
      if (show === "ready") return stage === "ready";
      return stage === "handed-over";
    });

    const when = (s: SurveyListed): number =>
      s.syncedAt === null ? 0 : Date.parse(s.syncedAt);
    return [...kept].sort((a, b) => {
      if (order === "oldest") return when(a) - when(b);
      if (order === "address") return a.reference.localeCompare(b.reference);
      return when(b) - when(a);
    });
  }, [surveys, find, order, show]);

  if (surveys.length === 0) {
    return (
      <div className="state state--empty">
        <span className="state__glyph">
          <IconState kind="empty" />
        </span>
        <p className="state__title">No surveys yet</p>
        <p className="state__body">
          A survey appears here the moment it arrives from the phone. Nothing
          needs doing in this portal first.
        </p>
      </div>
    );
  }

  const unopened = surveys.filter(isUnopened).length;

  return (
    <>
      <p className="lede" role="status" aria-live="polite">
        {surveys.length === 1 ? "One survey" : `${surveys.length} surveys`}
        {unopened > 0
          ? `, ${unopened === 1 ? "one" : unopened} nobody has opened yet`
          : ". Newest first."}
      </p>

      <div className="surveys__controls">
        <div className="surveys__find">
          <label htmlFor="survey-find">Find a house</label>
          <input
            id="survey-find"
            type="search"
            placeholder="Street name, postcode, or who walked it…"
            value={find}
            onChange={(e) => onFind(e.target.value)}
          />
        </div>

        <div className="surveys__pick">
          <label htmlFor="survey-show">Show</label>
          <select
            id="survey-show"
            value={show}
            onChange={(e) => setShow(e.target.value as Show)}
          >
            <option value="all">Everything</option>
            <option value="needs-answer">Needs an answer</option>
            <option value="ready">Ready to send</option>
            <option value="handed-over">Handed over</option>
          </select>
        </div>

        <div className="surveys__pick">
          <label htmlFor="survey-order">Order</label>
          <select
            id="survey-order"
            value={order}
            onChange={(e) => setOrder(e.target.value as Order)}
          >
            <option value="newest">Newest first</option>
            <option value="oldest">Oldest first</option>
            <option value="address">By address, A–Z</option>
          </select>
        </div>
      </div>

      {matching.length === 0 ? (
        <div className="state state--empty">
          <p className="state__title">No survey matches that</p>
          {/* Which of the two narrowings emptied it, so the way back is
              obvious rather than guessed at. */}
          <p className="state__body">
            {show === "all"
              ? "Try part of a street name or a postcode."
              : "Nothing is at that stage. Set Show back to Everything, or try another street name."}
          </p>
        </div>
      ) : (
        <ul className="surveys" data-testid="surveys">
          {matching.map((survey) => (
            <Row key={survey.surveyId} survey={survey} />
          ))}
        </ul>
      )}
    </>
  );
}

const STAGES: readonly { readonly key: Stage; readonly label: string }[] = [
  { key: "scanned", label: "Scanned" },
  { key: "answers", label: "Answers" },
  { key: "ready", label: "Ready" },
  { key: "handed-over", label: "Handed over" },
];

const ORDER: Record<Stage, number> = {
  scanned: 0,
  answers: 1,
  ready: 2,
  "handed-over": 3,
};

function Row({ survey }: { readonly survey: SurveyListed }): ReactNode {
  const { client } = usePortal();
  const stage = stageOf(survey);
  const unopened = isUnopened(survey);
  const reached = ORDER[stage];

  /*
   * Opening it is what clears the mark.
   *
   * `Job.tsx` was the only screen that recorded a view, because the pipeline
   * always went through the job. This list goes straight to the SURVEY, so
   * without this the "New" mark would be permanent — every survey would read
   * as unopened for ever and the one signal on the screen would mean nothing.
   *
   * Fire-and-forget, and a failure is swallowed: the reader is navigating away
   * and a bookkeeping write must not be able to hold that up or raise an error
   * over the screen they are leaving.
   */
  const open = (): void => {
    void client.markJobViewed(survey.jobId).catch(() => {});
  };

  return (
    <li className={`survey-card${unopened ? " survey-card--new" : ""}`}>
      <div className="survey-card__head">
        {unopened ? (
          <span
            className="survey-card__new"
            data-testid="survey-unopened"
            title="This survey arrived after you last looked"
          >
            ● New
          </span>
        ) : null}
        <h2>{survey.reference}</h2>
        {survey.postcode ? (
          <span className="survey-card__postcode">{survey.postcode}</span>
        ) : (
          <span className="survey-card__warn" data-testid="survey-no-address">
            Needs an address
          </span>
        )}
      </div>

      <p className="survey-card__facts">
        {survey.floors === 1 ? "1 floor" : `${survey.floors} floors`}
        {" · "}
        {survey.rooms === 1 ? "1 room" : `${survey.rooms} rooms`}
        {survey.surveyorName ? ` · walked by ${survey.surveyorName}` : ""}
        {survey.syncedAt ? ` · arrived ${utcMoment(survey.syncedAt)}` : ""}
      </p>

      {/* Colour, a word AND a tick or a count on every stage. */}
      <ol className="survey-stages" aria-label="How far this survey has got">
        {STAGES.map((s, i) => {
          const done = i < reached;
          const here = i === reached;
          return (
            <li
              key={s.key}
              className={`survey-stage${done ? " survey-stage--done" : ""}${here ? " survey-stage--here" : ""}`}
              aria-current={here ? "step" : undefined}
            >
              <span className="survey-stage__state">
                {done ? "✓ Done" : here ? labelFor(survey, s.key) : "Not yet"}
              </span>
              <span className="survey-stage__name">{s.label}</span>
            </li>
          );
        })}
      </ol>

      <a
        className="primary survey-card__open"
        href={href.survey(survey.surveyId)}
        onClick={open}
      >
        {unopened ? "Open this survey" : "Open"}
      </a>
    </li>
  );
}

/** What the stage a survey is ON says about itself, in plain words. */
function labelFor(survey: SurveyListed, stage: Stage): string {
  if (stage === "answers") {
    if (survey.answersLeft > 0) {
      return survey.answersLeft === 1 ? "1 left" : `${survey.answersLeft} left`;
    }
    return "No address";
  }
  if (stage === "ready") return "Ready to send";
  if (stage === "handed-over") return "✓ Sent";
  return "Here";
}
